import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, of, Subscription, timer} from 'rxjs';
import {catchError, concatMap, expand, filter, map, startWith, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {NotificationsService} from 'angular2-notifications';
import {AuthorizationService} from '../authorization/authorization.service';
import {BrowserTabService} from '../browser-tab/browser-tab.service';
import {TranslateService} from '@ngx-translate/core';
import {WebStompService} from '../web-stomp/web-stomp.service';
import * as moment from 'moment';
import {AlarmLogModel} from '../dashboard/monitoring/alarm-log.model';
import {AlarmType} from './alarm-type';
import {BackendInfoService} from '../shared/backend-info.service';

@Injectable({
  providedIn: 'root'
})
export class UnitLogManagerService {
  public static readonly alarmTypeUnit = 'unit';
  public static readonly alarmTypeEndUser = 'end-user';
  userHasActiveAlarms: Observable<boolean>; // BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  activeAlarms: BehaviorSubject<Array<AlarmLogModel>> = new BehaviorSubject<Array<AlarmLogModel>>([]);
  firstTime = true;
  private readonly refreshInterval = 10000;
  private skipNextRefresh = false;
  private refreshObservable: Observable<any>;
  private refreshSubscription: Subscription;

  constructor(
    protected http: HttpClient,
    protected notif: NotificationsService,
    protected authorizationService: AuthorizationService,
    protected browserTabService: BrowserTabService,
    protected translate: TranslateService,
    protected webStompService: WebStompService,
  ) {
    this.userHasActiveAlarms = this.activeAlarms.pipe(
      map(activeAlarms => activeAlarms.length > 0)
    );
    this.refreshObservable = this.authorizationService.getIdentityObservable().pipe(
      filter(identity => !!identity),
      switchMap(
        () => {
          const webStompIsOnline = this.webStompService.isOnline.getValue() && this.webStompService.isEnabled.getValue();
          // console.log({
          //   isRoot: this.authorizationService.isRoot(),
          //   skipNextRefresh: this.skipNextRefresh,
          //   firstTime: this.firstTime,
          //   webStompIsOnline: webStompIsOnline,
          //   refresh: !this.authorizationService.isRoot() && !this.skipNextRefresh && (this.firstTime || !webStompIsOnline)
          // });
          if (
            !this.authorizationService.isRoot()
            && !this.skipNextRefresh
            && (this.firstTime || !webStompIsOnline)
            && !BackendInfoService.backendIsLocalTest()
          ) {
            return this.http.get(
              environment.apiBasepoint + 'alarm-log?per-page=500&expand=retranslator_id,retranslator_setting_id'
            // environment.apiBasepoint + 'unit-log-alarm?per-page=500&expand=retranslator_id,retranslator_setting_id'
              // environment.apiBasepoint + 'unit-log?per-page=500&UnitLogSearch[priority]=3&UnitLogSearch[processed_by_user_id]=null'
            ).pipe(
              catchError((error: HttpErrorResponse) => of([])),
              tap(
                (alarms: Array<AlarmLogModel>) => {
                  this.activeAlarms.next(alarms);
                  this.firstTime = false;
                }
              )
            );
          } else {
            this.skipNextRefresh = false;
            return of(null);
          }
        }
      )
    );
    this.refreshSubscription = this.refreshObservable.pipe(
      expand(
        () => timer(this.refreshInterval).pipe(
          concatMap(() => this.refreshObservable)
        )
      )
    ).subscribe();
    if (!this.authorizationService.isRoot()) {
      this.webStompService.newAlarm
        .pipe(
          withLatestFrom(this.activeAlarms)
        )
        .subscribe(
        ([newAlarm, activeAlarms]) => {
          // console.log({newAlarm: newAlarm});
          activeAlarms = [newAlarm, ...activeAlarms];
          this.activeAlarms.next(activeAlarms);
        }
      );
      this.webStompService.retranslatorChange
        .pipe(
          withLatestFrom(this.activeAlarms)
        )
        .subscribe(
          ([retranslator, activeAlarms]) => {
            activeAlarms.forEach(
              (a, index) => {
                if (a.id === retranslator.unit_log_id && retranslator.time_finish) {
                  activeAlarms[index].retranslator_id = null;
                }
              }
            );
            this.activeAlarms.next(activeAlarms);
          }
        );

    }
  }
  closeAlarm(alarmType: AlarmType, alarmId: number, comment: string): Observable<any> {
    return this.http.patch(
      `${environment.apiBasepoint}${alarmType}-log/${alarmId}`,
      {
        processed_comment: comment
      }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case 500:
            this.notif.error(this.translate.instant('Server error'));
            console.error(error.error);
            break;
          default:
            this.notif.error(this.translate.instant('Unknown server error'));
            console.error(error.error);
        }
        throw error;
      }),
      tap((data: any) => {
        if (data.id) {
          const activeAlarms = this.activeAlarms.getValue();
          const alarmIndex = activeAlarms.findIndex(item => item.type === alarmType && item.id === alarmId);
          if (alarmIndex !== -1) {
            activeAlarms.splice(alarmIndex, 1);
            this.activeAlarms.next(activeAlarms);
            this.notif.remove(this.getAlarmNotificationKey(alarmType, alarmId));
          }
          this.notif.success(this.translate.instant('Saved'));
        } else {
          this.notif.error(this.translate.instant('Server Error'));
          console.error('Unknown error');
        }
      })
    );
  }
  // closeUnitAlarm(unitLogId: number, comment: string): Observable<any> {
  //   // we need to throttle upcoming unit-log-alarm request, because it get data from materialized view unit_log_alarm.
  //   // unit_log_alarm is synchronized every 6 seconds, so next refresh will bring old data, so we skip it
  //   this.skipNextRefresh = true;
  //   return this.http.patch(
  //     environment.apiBasepoint + 'unit-log/' + unitLogId.toString(),
  //     {
  //       processed_comment: comment
  //     }
  //   ).pipe(
  //     catchError((error: HttpErrorResponse) => {
  //       switch (error.status) {
  //         case 500:
  //           this.notif.error(this.translate.instant('Server error'));
  //           console.error(error.error);
  //           break;
  //         default:
  //           this.notif.error(this.translate.instant('Unknown server error'));
  //           console.error(error.error);
  //       }
  //       throw error;
  //     }),
  //     tap((data: any) => {
  //       if (data.id) {
  //         const activeAlarms = this.activeAlarms.getValue();
  //         const alarmIndex = activeAlarms.findIndex(item => item.id === unitLogId);
  //         if (alarmIndex !== -1) {
  //           activeAlarms.splice(alarmIndex, 1);
  //           this.activeAlarms.next(activeAlarms);
  //           this.notif.remove('alarm_' + unitLogId);
  //         }
  //         this.notif.success(this.translate.instant('Saved'));
  //       } else {
  //         this.notif.error(this.translate.instant('Server Error'));
  //         console.error('Unknown error');
  //       }
  //     })
  //   );
  // }

  closeAllWithComment(comment: string): Observable<any> {
    this.skipNextRefresh = true;
    return this.http.post(
      environment.apiBasepoint + 'alarm-log-batch',
      { processed_comment: comment }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case 500:
            this.notif.error(this.translate.instant('Server error'));
            console.error(error.error);
            break;
          default:
            this.notif.error(this.translate.instant('Unknown server error'));
            console.error(error.error);
        }
        throw error;
      }),
      tap((data: any) => {
        if (data.message) {
          this.activeAlarms.next([]);
          this.notif.remove();
          this.notif.success(this.translate.instant('Saved'));
        } else {
          this.notif.error(this.translate.instant('Server Error'));
          console.error('Unknown error');
        }
      })
    );
  }

  createRetranslator(retranslator_setting_id: number, unit_log_id: number) {
    return this.http.post<any>(
      environment.apiBasepoint + 'retranslator',
      { retranslator_setting_id: retranslator_setting_id, unit_log_id: unit_log_id }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case 500:
            this.notif.error(this.translate.instant('Server error'));
            console.error(error.error);
            break;
          default:
            this.notif.error(this.translate.instant('Unknown server error'));
            console.error(error.error);
        }
        throw error;
      }),
      withLatestFrom(this.activeAlarms),
      tap(([data, alarms]) => {
        if (data.id) {
          alarms = alarms.map(
            a => {
              if (a.id === unit_log_id) {
                a.retranslator_id = data.id;
              }
              return a;
            }
          );
          this.activeAlarms.next(alarms);
          this.notif.remove();
          this.notif.success(this.translate.instant('Saved'));
          // this.refreshAlarms();
        } else {
          this.notif.error(this.translate.instant('Server Error'));
          console.error('Unknown error');
        }
      })
    );
  }

  stopRetranslator(id: number) {
    return this.http.patch<any>(
      environment.apiBasepoint + 'retranslator/' + id.toString(10),
      { time_finish: moment().utc().format('YYYY-MM-DD HH:mm:ss') }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case 500:
            this.notif.error(this.translate.instant('Server error'));
            console.error(error.error);
            break;
          default:
            this.notif.error(this.translate.instant('Unknown server error'));
            console.error(error.error);
        }
        throw error;
      }),
      withLatestFrom(this.activeAlarms),
      tap(([data, alarms]) => {
        if (data.id && data.time_finish) {
          alarms = alarms.map(
            a => {
              if (a.id === data.unit_log_id) {
                a.retranslator_id = null;
              }
              return a;
            }
          );
          this.activeAlarms.next(alarms);
          this.notif.remove();
          this.notif.success(this.translate.instant('Saved'));
          // this.refreshAlarms();
        } else {
          this.notif.error(this.translate.instant('Server Error'));
          console.error('Unknown error');
        }
      })
    );
  }
  public getAlarmNotificationKey(alarmType: AlarmType, alarmId: number) {
    return `alarm_${alarmType}_${alarmId}`;
  }
}
